import { API_BASE } from '../config';
import axios from 'axios';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { judgeCorrectAnswer } from './answerJudgement';
import './pages.css';

// === 공통 설정 ===

const authAxios = axios.create({ baseURL: API_BASE });
authAxios.interceptors.request.use((config) => {
    const token = localStorage.getItem('token');
    if (token) config.headers.Authorization = `Bearer ${token}`;
    return config;
});

// 날짜 포맷 함수
const formatSubmittedAt = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleString('ko-KR', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
    });
};

// 이미지 응답 파싱 유틸리티
const parseImgAnswer = (v) => {
    try {
        const obj = JSON.parse(v || '{}');
        return { url: obj.url || '', text: obj.text || '' };
    } catch {
        return { url: '', text: '' };
    }
};

const makeAbsUrl = (u) => (u?.startsWith('http') ? u : `${API_BASE}${u || ''}`);

// === 데이터 훅 ===

// ⭐ 설문/사이트/사업 마스터 (프론트 필터링 개선)
function useProgramsSitesSurveys(filters) {
    const { program_id, site_id } = filters;
    const [programs, setPrograms] = useState([]);
    const [sites, setSites] = useState([]);
    const [allSurveys, setAllSurveys] = useState([]);

    // 사업
    useEffect(() => {
        authAxios
            .get('/api/programs')
            .then(({ data }) => setPrograms(data || []))
            .catch(() => setPrograms([]));
    }, []);

    // 사이트 (사업 선택 시 해당 사이트만)
    useEffect(() => {
        const params = program_id ? { program_id } : {};
        authAxios
            .get('/api/sites', { params })
            .then(({ data }) => setSites(data || []))
            .catch(() => setSites([]));
    }, [program_id]);

    // 설문 (site_id가 있으면 서버 필터, 아니면 풀 목록 받아서 프론트 필터)
    useEffect(() => {
        const params = {};
        if (site_id) params.site_id = site_id;
        authAxios
            .get('/api/surveys', { params })
            .then(({ data }) => setAllSurveys(data || []))
            .catch(() => setAllSurveys([]));
    }, [site_id, program_id]);

    // 최종 설문 목록
    const surveys = useMemo(() => {
        if (site_id) return allSurveys;
        if (!program_id) return allSurveys;
        const siteSet = new Set(sites.map(s => Number(s.site_id)));
        return allSurveys.filter(sv => siteSet.has(Number(sv.site_id)));
    }, [allSurveys, program_id, site_id, sites]);

    return { programs, sites, surveys };
}

// 제출 목록
function useSubmissions(program_id, site_id, survey_id, refreshKey) {
    const [submissions, setSubmissions] = useState([]);
    const [loading, setLoading] = useState(false);
    
    useEffect(() => {
        const params = {
            program_id: program_id || '',
            site_id: site_id || '',
            survey_id: survey_id || ''
        };
        setLoading(true);
        authAxios
            .get('/api/submissions', { params })
            .then(({ data }) => {
                setSubmissions(data?.ok ? (data.data || []) : []);
            })
            .catch((e) => {
                console.error("API Error: /api/submissions", e);
                setSubmissions([]);
            })
            .finally(() => setLoading(false));
    }, [program_id, site_id, survey_id, refreshKey]); 

    return { submissions, loading };
}

// 유저의 나이 계산(출생년도 기준)
const calcAge = (birth) => {
    const by = Number(birth);
    if (!Number.isFinite(by) || by < 1900) return null;
    const y = new Date().getFullYear();
    const age = y - by;
    return (age >= 0 && age <= 120) ? age : null;
};

// ✅ 배정 사용자 (미제출 표시 용) — 사이트/사업 기준으로 필터
function useAssignedUsers(program_id, site_id, loadAll = false) {
    const [assignedUsers, setAssignedUsers] = useState([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        // 평소에는 필터가 없으면 전체 사용자를 불러오지 않지만,
        // 이름 검색 중에는 전체 사용자에서 검색할 수 있도록 허용
        if (!program_id && !site_id && !loadAll) {
            setAssignedUsers([]);
            return;
        }

        const qs = new URLSearchParams();
        if (program_id) qs.set('program_id', program_id);
        if (site_id)    qs.set('site_id', site_id);
        setLoading(true);
        authAxios.get(`/api/admin/users${qs.toString() ? `?${qs}` : ''}`)
            .then(({ data }) => {
                const list = Array.isArray(data?.users) ? data.users : [];
                // 필요한 최소 필드만 추려서 보관
                setAssignedUsers(list.map(u => ({
                    user_id: u.user_id,
                    name: u.name,
                    sex: u.sex,
                    birth: u.birth,
                    age: calcAge(u.birth), // ⭐ 나이를 미리 계산하여 Map에 넣을 때 활용 가능하도록 준비
                    phone: u.phone
                })));
            })
            .catch(err => {
                console.warn('배정 사용자 조회 실패(미제출 표시 생략):', err?.message);
                setAssignedUsers([]);
            })
            .finally(() => setLoading(false));
    }, [program_id, site_id, loadAll]);

    return { assignedUsers, loading };
}

// 이름 검색 시 사용자의 사이트별 배정 상태 조회
function useUserSiteAssignments(userIds, enabled) {
    const [assignments, setAssignments] = useState([]);
    const [loading, setLoading] = useState(false);

    const userIdsKey = useMemo(
        () => [...new Set((userIds || []).map(Number).filter(Number.isFinite))]
            .sort((a, b) => a - b)
            .join(','),
        [userIds]
    );

    useEffect(() => {
        if (!enabled || !userIdsKey) {
            setAssignments([]);
            setLoading(false);
            return;
        }

        let cancelled = false;
        const ids = userIdsKey.split(',').map(Number);

        setLoading(true);
        Promise.all(
            ids.map(userId =>
                authAxios.get(`/api/admin/users/${userId}/sites`)
                    .then(({ data }) =>
                        (Array.isArray(data) ? data : []).map(site => ({
                            ...site,
                            user_id: userId,
                        }))
                    )
                    .catch(() => [])
            )
        )
            .then(results => {
                if (cancelled) return;
                const deduped = new Map();

                for (const row of results.flat()) {
                    const key = `${row.user_id}|${row.site_id}`;
                    if (!deduped.has(key)) deduped.set(key, row);
                }

                setAssignments(Array.from(deduped.values()));
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });

        return () => {
            cancelled = true;
        };
    }, [enabled, userIdsKey]);

    return { assignments, loading };
}


// 이름 검색 시 해당 사용자들의 제출 기록을 직접 조회
// /api/submissions의 전체 목록은 최대 500건이라 오래된 제출이 누락될 수 있으므로,
// 이름 검색 중에는 user_id 기준으로 다시 조회합니다.
function useUserSubmissions(userIds, site_id, survey_id, enabled, refreshKey) {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);

    const userIdsKey = useMemo(
        () => [...new Set((userIds || []).map(Number).filter(Number.isFinite))]
            .sort((a, b) => a - b)
            .join(','),
        [userIds]
    );

    useEffect(() => {
        if (!enabled || !userIdsKey) {
            setRows([]);
            setLoading(false);
            return;
        }

        let cancelled = false;
        const ids = userIdsKey.split(',').map(Number);

        setLoading(true);

        Promise.all(
            ids.map(userId => {
                const params = { user_id: userId };
                if (site_id) params.site_id = site_id;
                if (survey_id) params.survey_id = survey_id;

                return authAxios
                    .get('/api/submissions', { params })
                    .then(({ data }) => data?.ok ? (data.data || []) : [])
                    .catch(() => []);
            })
        )
            .then(results => {
                if (!cancelled) setRows(results.flat());
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });

        return () => {
            cancelled = true;
        };
    }, [enabled, userIdsKey, site_id, survey_id, refreshKey]);

    return { rows, loading };
}


// 제출 상세
function useSubmissionDetails(submission_key) {
    const [details, setDetails] = useState(null);
    const [loading, setLoading] = useState(false);
    
    const groupedDetails = useMemo(() => {
        if (!details) return null;
        const map = new Map();
        for (const r of details) {
            const qid = r.question_id ?? `q-${r.response_id}`;
            if (!map.has(qid)) {
                map.set(qid, {
                    question_id: qid,
                    question_text: r.question_text,
                    question_type_id: r.question_type_id,
                    type_label: r.question_type_display || r.question_type_key || r.question_type_id || '',
                    correct_answer: r.correct_answer,
                    user_answers: [],
                    rows: []
                });
            }
            const bucket = map.get(qid);
            bucket.user_answers.push(r.answer_text);
            bucket.rows.push(r);
            if (!bucket.question_type_id && r.question_type_id != null) bucket.question_type_id = r.question_type_id;
            if ((bucket.correct_answer == null || bucket.correct_answer === '') && r.correct_answer) bucket.correct_answer = r.correct_answer;
            if (!bucket.type_label && (r.question_type_display || r.question_type_key || r.question_type_id)) bucket.type_label = r.question_type_display || r.question_type_key || r.question_type_id;
        }

        return Array.from(map.values()).map(b => ({
            ...b,
            is_correct: judgeCorrectAnswer(b.user_answers, b.correct_answer, b.question_type_id)
        }));
    }, [details]);

    useEffect(() => {
        if (!submission_key) {
            setDetails(null);
            setLoading(false);
            return;
        }

        setLoading(true);
        authAxios
            .get(`/api/submissions/${encodeURIComponent(submission_key)}`)
            .then(({ data }) => {
                setDetails(data?.ok ? (data.data || []) : null);
            })
            .catch((e) => {
                console.error("API Error: /api/submissions/:key", e);
                setDetails(null);
            })
            .finally(() => setLoading(false));
    }, [submission_key]);

    return { details: groupedDetails, loading };
}

// 삭제
const deleteSubmission = async (submission_key, user_name, survey_title) => {
    if (!submission_key) return false;

    const confirmMessage = `이 제출 묶음(${user_name}의 ${survey_title} 응답 전체)을 삭제하시겠습니까?\n\n주의: 되돌릴 수 없습니다.`;

    if (window.confirm(confirmMessage)) {
        try {
            const { data } = await authAxios.delete(`/api/submissions/${encodeURIComponent(submission_key)}`);
            if (data?.ok) {
                alert(`응답이 성공적으로 삭제되었습니다. (삭제된 행: ${data.deleted})`);
                return true;
            } else {
                alert('응답 삭제에 실패했습니다. 서버 응답 확인 필요.');
                return false;
            }
        } catch (error) {
            alert('응답 삭제 요청 중 오류가 발생했습니다.');
            console.error('Delete error:', error);
            return false;
        }
    }
    return false;
};

// 상세 패널
function SubmissionDetailsPanel({ submission_key, submission, surveyName, onDeleteSuccess, isExpanded, onToggle }) {
    const { details, loading: detailsLoading } = useSubmissionDetails(isExpanded ? submission_key : null); 
    const [viewer, setViewer] = useState({ open:false, url:'', text:'' });

    const handleDelete = async () => {
        const success = await deleteSubmission(submission_key, submission.user_name, surveyName);
        if (success) {
            onToggle(null);
            onDeleteSuccess();
        }
    };

    if (!isExpanded) return null;

    return (
        <>
            <div className="response-details stack-4">
                <hr style={{ margin: '8px 0' }}/>
                <div className="sub-muted">제출 상세 내용</div>

                {detailsLoading ? (
                    <div>응답 내용을 불러오는 중...</div>
                ) : (
                    <>
                        {(details || []).map((q, i) => (
                            <div key={q.question_id} className="detail-item">
                                <div className="question-text">
                                    {i + 1}. {q.question_text} <span className='muted' style={{marginLeft: 10}}>({q.type_label})</span>
                                </div>
                                <div className="answer-text">
                                    {Number(q.question_type_id) === 6
                                        ? (q.rows || []).map((r, idx) => {
                                                const { url, text } = parseImgAnswer(r.answer_text);
                                                return (
                                                    <div key={idx} style={{ marginBottom: 10 }}>
                                                        {url && (
                                                            <a
                                                                href={makeAbsUrl(url)}
                                                                target="_blank"
                                                                rel="noreferrer"
                                                                onClick={(e) => {
                                                                    e.preventDefault();
                                                                    setViewer({ open:true, url: makeAbsUrl(url), text });
                                                                }}
                                                                title="클릭하면 크게 보기"
                                                                style={{ display: 'inline-block' }}
                                                            >
                                                                <img
                                                                    src={makeAbsUrl(url)}
                                                                    alt="업로드 이미지"
                                                                    style={{ maxWidth: 100, maxHeight: 100, borderRadius: 6, border: '1px solid #eee', display: 'block', cursor:'zoom-in' }}
                                                                />
                                                            </a>
                                                        )}
                                                        {String(text || '').trim() && (
                                                            <div className="question-explain" style={{ marginTop: 6, whiteSpace: 'pre-wrap' }}>
                                                                {text}
                                                            </div>
                                                        )}
                                                    </div>
                                                );
                                            })
                                        : (Array.isArray(q.user_answers) ? q.user_answers.join(', ') : String(q.user_answers || '응답 없음'))}
                                </div>
                                <div style={{ 
                                    fontSize: '0.9em',
                                    fontWeight: q.is_correct != null ? 700 : 400,
                                    color: q.is_correct === true ? '#1976d2' : q.is_correct === false ? '#d32f2f' : '#6c757d' 
                                }}>
                                    {q.is_correct === true ? '✅ 정답'
                                    : q.is_correct === false ? '❌ 오답'
                                    : ''}
                                </div>
                            </div>
                        ))}

                        <button
                            className="btn btn-delete"
                            onClick={handleDelete}
                            style={{ marginTop: '10px' }}
                        >
                            제출 묶음 삭제 (응답 전체)
                        </button>
                    </>
                )}
            </div>
            
            {viewer.open && (
                <div
                    onClick={() => setViewer({ open:false, url:'', text:'' })}
                    style={{
                        position:'fixed', inset:0, background:'rgba(0,0,0,0.6)',
                        display:'flex', alignItems:'center', justifyContent:'center',
                        zIndex: 9999, padding: 24
                    }}
                >
                    <div
                        onClick={(e) => e.stopPropagation()}
                        style={{ background:'#fff', borderRadius:12, padding:16, maxWidth:'92vw', maxHeight:'90vh', boxShadow:'0 10px 30px rgba(0,0,0,.3)' }}
                    >
                        <img
                            src={viewer.url}
                            alt="응답 이미지"
                            style={{ maxWidth:'90vw', maxHeight:'75vh', display:'block', margin:'0 auto', borderRadius:8 }}
                        />
                        {viewer.text && (
                            <div style={{ marginTop:12, whiteSpace:'pre-wrap', color:'#333' }}>{viewer.text}</div>
                        )}
                        <div style={{ display:'flex', gap:8, justifyContent:'flex-end', marginTop:12 }}>
                            <a href={viewer.url} target="_blank" rel="noreferrer" style={{ textDecoration:'none' }}>
                                새 탭에서 열기
                            </a>
                            <button onClick={() => setViewer({ open:false, url:'', text:'' })}>닫기</button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}

// 리스트(제출/미제출 혼합 렌더)
function SubmissionList({ rows, loading, surveyName, onDeleteSuccess, hasFiltersSelected }) {
    const [expandedKey, setExpandedKey] = useState(null);

    const toggleDetails = useCallback((submission) => {
        setExpandedKey(prevKey => (prevKey === submission.submission_key ? null : submission.submission_key));
    }, []);

    useEffect(() => {
        setExpandedKey(null);
    }, [rows]);

    if (loading) return <div className="panel">제출 목록을 불러오는 중...</div>;
    
    if (!rows.length && hasFiltersSelected) {
        return (
            <div className="panel info-message">
                선택된 조건에 해당하는 제출 완료된 응답이 없습니다. <br/>
                (💡 배정 사용자 중 미제출자가 없다면 이 메시지가 보일 수 있습니다.)
            </div>
        );
    }
    
    if (!rows.length) return <div className="panel muted">조회 조건에 맞는 항목이 없습니다.</div>;

    const submittedCount = rows.filter(r => r.__status === '완료').length;
    const unsubmittedCount = rows.filter(r => r.__status === '미제출').length;

    return (
        <div className="stack">
            <div className="sub-muted status-summary">
                ✅ 제출 {submittedCount}건 · ⏳ 미제출 {unsubmittedCount}명
            </div>
            <div className="user-list-grid">
                {rows.map((s) => {
                    const isExpanded = s.submission_key && s.submission_key === expandedKey;
                    
                    // 나이/성별 정보를 조합합니다.
                    // s.user_age에 이제 값이 들어있거나, 미제출자의 경우 s.age에 값이 들어있습니다.
                    const userInfo = [
                        (s.user_age ?? s.age) && (s.user_age ?? s.age) + '세', 
                        s.user_sex ?? s.sex
                    ].filter(Boolean).join('/');

                    return (
                        <div key={s.submission_key || `pending-${s.user_id}`} className={`user-row panel ${s.__status === '완료' ? 'submitted' : 'pending'} ${isExpanded ? 'expanded' : ''}`}>
                            <div
                                className="user-info-section"
                                onClick={() => { if (s.__status === '완료') toggleDetails(s); }}
                                style={{ cursor: s.__status === '완료' ? 'pointer' : 'default' }}
                            >
                                <div className="user-name">
                                    <strong>{s.user_name || s.name}</strong> {userInfo && `(${userInfo})`}
                                </div>
                                <div className="site-info">
                                    {s.site_name}
                                </div>

                                <div className={`status-badge ${s.__status === '완료' ? 'completed' : 'pending'}`}>
                                    {s.__status === '완료' ? '✅ 완료' : '⏳ 미제출'}
                                </div>

                                <div className="submission-time muted">
                                    {s.__status === '완료' ? `제출시각: ${formatSubmittedAt(s.last_submitted_at)}` : '아직 제출되지 않았습니다'}
                                </div>
                            </div>

                            {s.__status === '완료' && (
                                <SubmissionDetailsPanel
                                    submission_key={s.submission_key}
                                    submission={s}
                                    surveyName={surveyName}
                                    onDeleteSuccess={onDeleteSuccess}
                                    isExpanded={isExpanded}
                                    onToggle={setExpandedKey}
                                />
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

// === 페이지 ===
export default function ResponsesPage() {
    const navigate = useNavigate();

    const handleLogout = () => {
        localStorage.removeItem('token');
        navigate('/');
    };

    const [filters, setFilters] = useState({
        program_id: '',
        site_id: '',
        survey_id: '',
    });
    const [nameSearch, setNameSearch] = useState('');

    const [refreshKey, setRefreshKey] = useState(0);
    const handleSubmissionChange = useCallback(() => {
        setRefreshKey(prev => prev + 1);
    }, []);

    // 마스터
    const { programs, sites, surveys } = useProgramsSitesSurveys(filters);

    // 선택된 설문/사업 이름 표시
    const selectedSurvey = useMemo(() => {
        return surveys.find(s => s.survey_id === parseInt(filters.survey_id));
    }, [filters.survey_id, surveys]);
    const surveyName = selectedSurvey ? selectedSurvey.title : '';

    const programName = useMemo(() => {
        // 우선: 현재 필터로 선택된 사업
        const p = programs.find(pp => String(pp.program_id) === String(filters.program_id));
        if (p) return p.name + (p.year ? ` (${p.year})` : '');

        // 설문이 선택된 경우, 해당 설문이 속한 사이트의 사업명 유추
        if (selectedSurvey) {
            const site = sites.find(ss => Number(ss.site_id) === Number(selectedSurvey.site_id));
            if (site) {
                const prog = programs.find(pp => Number(pp.program_id) === Number(site.program_id));
                if (prog) return prog.name + (prog.year ? ` (${prog.year})` : '');
            }
        }
        return '';
    }, [filters.program_id, programs, selectedSurvey, sites]);

    // 데이터
    const { submissions, loading: submissionsLoading } = useSubmissions(
        filters.program_id, 
        filters.site_id,
        filters.survey_id,
        refreshKey
    );

    // 배정 사용자(미제출 표시용)
    // 이름 검색 중이면 사업/사이트가 '전체'여도 사용자 목록을 불러옵니다.
    const isNameSearching = !!nameSearch.trim();
    const { assignedUsers, loading: assignedLoading } =
        useAssignedUsers(filters.program_id, filters.site_id, isNameSearching);

    // 이름 검색어와 일치하는 사용자
    const searchedAssignedUsers = useMemo(() => {
        const keyword = nameSearch.trim().toLowerCase();
        if (!keyword) return [];
        return (assignedUsers || []).filter(u =>
            String(u.name || '').toLowerCase().includes(keyword)
        );
    }, [assignedUsers, nameSearch]);

    const searchedUserIds = useMemo(
        () => searchedAssignedUsers.map(u => u.user_id),
        [searchedAssignedUsers]
    );

    // 이름 검색 중에는 해당 사용자의 제출 기록을 user_id 기준으로 직접 조회합니다.
    // 이렇게 해야 '사이트 전체'에서도 오래된 제출이 500건 제한 때문에 누락되지 않습니다.
    const { rows: searchedUserSubmissions, loading: searchedSubmissionsLoading } =
        useUserSubmissions(
            searchedUserIds,
            filters.site_id,
            filters.survey_id,
            isNameSearching,
            refreshKey
        );

    // 사이트를 직접 고르지 않고 이름으로 검색하면 사용자의 전체 배정 사이트를 조회
    const shouldLoadUserSites = isNameSearching && !filters.site_id;
    const { assignments: searchedUserSites, loading: searchedSitesLoading } =
        useUserSiteAssignments(searchedUserIds, shouldLoadUserSites);

    // === 클라이언트 병합/필터 ===
    const siteIdsInProgram = useMemo(() => {
        if (!filters.program_id) return null;
        return new Set(sites.filter(s => String(s.program_id) === String(filters.program_id)).map(s => Number(s.site_id)));
    }, [sites, filters.program_id]);

    const filteredSubmissions = useMemo(() => {
        let list = submissions || [];
        // 사업 기준만 선택된 경우에도 필터링 동작
        if (siteIdsInProgram && !filters.site_id) {
            list = list.filter(s => siteIdsInProgram.has(Number(s.site_id)));
        }
        return list;
    }, [submissions, siteIdsInProgram, filters.site_id]);

    // 제출/미제출 병합
    const mergedRows = useMemo(() => {
        // 필터 미선택이면 기존처럼 제출된 항목만 보기
        if (!filters.program_id && !filters.site_id && !filters.survey_id) {
            return (filteredSubmissions || []).map(s => ({ ...s, __status: '완료' }));
        }
        
        // assignedUsers를 Map으로 만들어 제출 완료 데이터 보완에 사용합니다.
        const assignedUserMap = new Map();
        for (const u of assignedUsers || []) {
            assignedUserMap.set(u.user_id, u);
        }

        // 제출 완료 인덱스(가능하면 user_id로, 없으면 이름+전화로)
        const submittedIndex = new Map();
        
        // 1) 배정 사용자 -> 기본 '미제출'로 생성
        const base = (assignedUsers || []).map(u => ({
            __status: '미제출',
            user_id: u.user_id,
            name: u.name,
            sex: u.sex,
            age: u.age, // useAssignedUsers에서 이미 계산된 값
            site_name: '', 
        }));

        // 2) 제출 완료를 덮어쓰기
        for (const s of filteredSubmissions) {
            const key = s.user_id ?? `${s.user_name || ''}::${s.user_phone || ''}`;
            
            // assignedUser는 이름/성별/나이 보완에 사용
            const assignedUser = assignedUserMap.get(s.user_id);
            
            // ⭐⭐⭐ [수정 핵심] 연령 결정 로직: 제출 데이터 나이 > 제출 데이터 생년 계산 > 배정 유저 나이 순
            let finalAge = s.user_age; 
            
            if (finalAge == null) {
                finalAge = calcAge(s.user_birth);
            }
            
            if (finalAge == null) {
                finalAge = assignedUser?.age;
            }

            // 성별은 제출 데이터 > assignedUser 순으로 사용
            const finalSex = s.user_sex ?? assignedUser?.sex; 
            
            const row = {
                ...s,
                __status: '완료',
                user_name: s.user_name || assignedUser?.name,
                user_sex: finalSex, 
                user_age: finalAge // 최종 결정된 나이 정보 할당
            };
            submittedIndex.set(key, row);
        }

        // 3) 출력 배열: 제출 있으면 제출 데이터로 교체, 없으면 미제출 유지
        const resultMap = new Map();
        for (const u of base) {
            const key = u.user_id ?? `${u.name || ''}`;
            const hit = submittedIndex.get(u.user_id) || submittedIndex.get(`${u.name || ''}::${u.phone || ''}`);
            resultMap.set(key, hit ? hit : u);
        }
        // 제출 데이터 중 배정목록에 없던 사람도 추가(안전)
        for (const [k, v] of submittedIndex.entries()) {
            const outKey = v.user_id ?? k;
            if (!resultMap.has(outKey)) resultMap.set(outKey, v);
        }

        return Array.from(resultMap.values());
    }, [filteredSubmissions, assignedUsers, filters.program_id, filters.site_id, filters.survey_id]);

    // 이름 검색 시 사용할 허용 사이트 집합
    // 사업 선택됨 -> 해당 사업 사이트만, 사업 전체 -> 전체 사이트
    const allowedSearchSiteIds = useMemo(() => {
        if (filters.program_id) {
            return new Set(
                sites
                    .filter(s => String(s.program_id) === String(filters.program_id))
                    .map(s => Number(s.site_id))
            );
        }
        return new Set((sites || []).map(s => Number(s.site_id)));
    }, [sites, filters.program_id]);

    // 사업/사이트 전체에서도 이름만 검색하면 사용자 × 배정사이트 단위로 표시
    const siteAwareSearchRows = useMemo(() => {
        if (!shouldLoadUserSites) return [];

        const matchingUsers = new Map(
            searchedAssignedUsers.map(u => [Number(u.user_id), u])
        );

        const resultMap = new Map();

        // 먼저 배정 사이트별 미제출 행 생성
        for (const assignment of searchedUserSites || []) {
            const user = matchingUsers.get(Number(assignment.user_id));
            const siteId = Number(assignment.site_id);

            if (!user || !allowedSearchSiteIds.has(siteId)) continue;

            const key = `${user.user_id}|${siteId}`;
            resultMap.set(key, {
                __status: '미제출',
                user_id: user.user_id,
                name: user.name,
                sex: user.sex,
                age: user.age,
                phone: user.phone,
                site_id: siteId,
                site_name: assignment.site_name || '',
                assignment_complete: assignment.complete,
            });
        }

        // 제출이 있으면 같은 사용자/사이트 행을 완료로 교체
        // 이름 검색용 user_id 직접 조회 결과를 사용하여 오래된 제출도 놓치지 않습니다.
        for (const submission of searchedUserSubmissions || []) {
            const userId = Number(submission.user_id);
            const siteId = Number(submission.site_id);
            const assignedUser = matchingUsers.get(userId);

            if (!assignedUser || !allowedSearchSiteIds.has(siteId)) continue;

            let finalAge = submission.user_age;
            if (finalAge == null) finalAge = calcAge(submission.user_birth);
            if (finalAge == null) finalAge = assignedUser.age;

            const completedRow = {
                ...submission,
                __status: '완료',
                user_name: submission.user_name || assignedUser.name,
                user_sex: submission.user_sex ?? assignedUser.sex,
                user_age: finalAge,
            };

            const key = `${userId}|${siteId}`;
            const current = resultMap.get(key);

            // 설문 전체일 경우 같은 사이트 제출이 여러 건이면 가장 최근 것을 표시
            if (
                !current ||
                current.__status !== '완료' ||
                new Date(completedRow.last_submitted_at || 0) >
                    new Date(current.last_submitted_at || 0)
            ) {
                resultMap.set(key, completedRow);
            }
        }

        return Array.from(resultMap.values()).sort((a, b) => {
            const nameCompare = String(a.user_name || a.name || '').localeCompare(
                String(b.user_name || b.name || ''),
                'ko'
            );
            if (nameCompare !== 0) return nameCompare;

            return String(a.site_name || '').localeCompare(
                String(b.site_name || ''),
                'ko'
            );
        });
    }, [
        shouldLoadUserSites,
        searchedAssignedUsers,
        searchedUserSites,
        allowedSearchSiteIds,
        searchedUserSubmissions,
    ]);

    const displayRows = useMemo(() => {
        const keyword = nameSearch.trim().toLowerCase();

        if (!keyword) return mergedRows;

        // 사이트를 고르지 않은 이름 검색은 사이트별 현황 표시
        if (shouldLoadUserSites) return siteAwareSearchRows;

        // 특정 사이트가 선택된 경우에도 이름 검색용 직접 조회 결과로 완료 상태를 보강합니다.
        if (filters.site_id) {
            const matchingUsers = new Map(
                searchedAssignedUsers.map(u => [Number(u.user_id), u])
            );

            const resultMap = new Map();

            // 배정 사용자를 기본 미제출로 생성
            for (const user of searchedAssignedUsers) {
                resultMap.set(Number(user.user_id), {
                    __status: '미제출',
                    user_id: user.user_id,
                    name: user.name,
                    sex: user.sex,
                    age: user.age,
                    phone: user.phone,
                    site_id: Number(filters.site_id),
                    site_name:
                        sites.find(s => Number(s.site_id) === Number(filters.site_id))?.site_name || '',
                });
            }

            // 실제 제출이 있으면 완료로 교체
            for (const submission of searchedUserSubmissions || []) {
                const userId = Number(submission.user_id);
                const assignedUser = matchingUsers.get(userId);
                if (!assignedUser) continue;

                let finalAge = submission.user_age;
                if (finalAge == null) finalAge = calcAge(submission.user_birth);
                if (finalAge == null) finalAge = assignedUser.age;

                resultMap.set(userId, {
                    ...submission,
                    __status: '완료',
                    user_name: submission.user_name || assignedUser.name,
                    user_sex: submission.user_sex ?? assignedUser.sex,
                    user_age: finalAge,
                });
            }

            return Array.from(resultMap.values());
        }

        return (mergedRows || []).filter(row =>
            String(row.user_name || row.name || '')
                .toLowerCase()
                .includes(keyword)
        );
    }, [
        mergedRows,
        nameSearch,
        shouldLoadUserSites,
        siteAwareSearchRows,
        filters.site_id,
        searchedAssignedUsers,
        searchedUserSubmissions,
        sites,
    ]);

    const hasFiltersSelected =
        !!filters.program_id ||
        !!filters.site_id ||
        !!filters.survey_id ||
        !!nameSearch.trim();

    const anyLoading =
        submissionsLoading ||
        assignedLoading ||
        (isNameSearching && searchedSubmissionsLoading) ||
        (shouldLoadUserSites && searchedSitesLoading);

    return (
        <div className="survey-result-container">
            <header className="page-header">
                <h1>응답 관리</h1>
                <div className="top-right-button-group">
                    <button onClick={() => navigate('/dashboard')} className="navigate-button">대시보드</button>
                    <button onClick={() => navigate('/user')} className="navigate-button">평가단 관리</button>
                    <button onClick={() => navigate('/site')} className="navigate-button">사이트 관리</button>
                    <button onClick={() => navigate('/admin')} className="navigate-button">설문 관리</button>
                    <button onClick={() => navigate('/responses')} className="navigate-button primary">응답 관리</button>
                    <button onClick={() => navigate('/results')} className="navigate-button">통계 보기</button>
                    <button onClick={() => navigate('/admin/login-notice')} className="navigate-button">로그인 공지 관리</button>
                    <button onClick={handleLogout} className="navigate-button logout-button">로그아웃</button>
                </div>
            </header>

            <div className="panel sticky filter-panel">
                <div className="filters-grid responses-filters-grid">
                    <select
                        className='input-beauty'
                        value={filters.program_id}
                        onChange={(e) =>
                            // 사업 선택 시 사이트/설문 초기화 (재조회 유발)
                            setFilters((f) => ({
                                ...f,
                                program_id: e.target.value,
                                site_id: '',
                                survey_id: ''
                            }))
                        }
                    >
                        <option value="">사업: 전체</option>
                        {programs.map((p) => (
                            <option key={p.program_id} value={p.program_id}>
                                {p.name} ({p.year})
                            </option>
                        ))}
                    </select>

                    <select
                        className='input-beauty'
                        value={filters.site_id}
                        onChange={(e) => setFilters((f) => ({ ...f, site_id: e.target.value, survey_id: '' }))}
                    >
                        <option value="">사이트: 전체</option>
                        {sites.map((s) => (
                            <option key={s.site_id} value={s.site_id}>
                                {s.site_name}
                            </option>
                        ))}
                    </select>

                    <select
                        className='input-beauty'
                        value={filters.survey_id}
                        onChange={(e) => setFilters((f) => ({ ...f, survey_id: e.target.value }))}
                    >
                        <option value="">설문: 전체</option>
                        {surveys.map((sv) => (
                            <option key={sv.survey_id} value={sv.survey_id}>
                                {sv.title}
                            </option>
                        ))}
                    </select>

                    <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                        <input
                            className='input-beauty'
                            type='text'
                            value={nameSearch}
                            onChange={(e) => setNameSearch(e.target.value)}
                            placeholder='이름 검색'
                            aria-label='평가자 이름 검색'
                            style={{ flex: 1, minWidth: 0 }}
                        />
                        {nameSearch && (
                            <button
                                type='button'
                                className='navigate-button'
                                onClick={() => setNameSearch('')}
                                style={{ whiteSpace: 'nowrap' }}
                            >
                                초기화
                            </button>
                        )}
                    </div>
                </div>
            </div>

            <div className="result-container">
                <h2 className="section-title">
                    제출된 응답 현황
                    { (surveyName || programName) && (
                        <span className="muted" style={{ marginLeft: 8, fontWeight: 500 }}>
                            {surveyName ? `· ${surveyName}` : ''}
                            {programName ? ` (${programName})` : ''}
                        </span>
                    )}
                </h2>
                <hr/>
                
                <SubmissionList
                    rows={displayRows}
                    loading={anyLoading}
                    surveyName={surveyName}
                    onDeleteSuccess={handleSubmissionChange}
                    hasFiltersSelected={hasFiltersSelected}
                />
            </div>
        </div>
    );
}
